import BodyText, { BODY_TEXT_SIZES } from "@shared/ui/BodyText";
import Tag from "@shared/ui/Tag";
import React, { useMemo, useState, useRef } from "react";
import { useIntl } from "react-intl";

import { getOptionBackgroundColor } from "~/utils";
import { Select3MultiPortal, Select3Multi } from "~/components/form/_inputs";

const CustomMultiSelectField = ({
  field,
  handleSelectFieldSave,
  isEditable = true,
  usePortal = false,
}) => {
  const { messages } = useIntl();

  const optionList = useMemo(
    () =>
      field?.options?.map(
        (item: {
          _id: string;
          label: string;
          value: string;
          color: string;
        }) => ({
          _id: item?._id,
          label: item?.value,
          value: item?.value,
          color: item?.color,
        }),
      ) || [],
    [field?.options],
  );

  const [isDropDownOpen, setIsDropDownOpen] = useState(false);

  const getInitialValues = () => {
    if (field?.values && Array.isArray(field.values) && optionList.length > 0) {
      return optionList.filter((option) => field.values.includes(option.value));
    }
    return [];
  };

  const [currentValues, setCurrentValues] = useState(getInitialValues);

  const lastFieldValue = useRef(field?.values);

  const syncWithFieldValue = () => {
    const fieldValueString = JSON.stringify(field?.values);
    const lastValueString = JSON.stringify(lastFieldValue.current);
    if (fieldValueString !== lastValueString) {
      lastFieldValue.current = field?.values;
      setCurrentValues(getInitialValues());
    }
  };

  const onClose = () => {
    setIsDropDownOpen(false);
    const newValues = currentValues.map((value) => value.value);
    const originalValues: string[] = field?.values ?? [];
    const hasChanged =
      newValues.length !== originalValues.length ||
      newValues.some((v) => !originalValues.includes(v)) ||
      originalValues.some((v) => !newValues.includes(v));
    if (hasChanged) {
      handleSelectFieldSave(field?._id, newValues);
    }
  };

  const onToggle = (isOpen) => {
    if (isOpen) {
      syncWithFieldValue();
      setIsDropDownOpen(true);
    } else {
      setIsDropDownOpen(false);
    }
  };

  const onClick = (optionValue: string) => {
    const existingIndex = currentValues.findIndex(
      (option) => option.value === optionValue,
    );
    if (existingIndex !== -1) {
      setCurrentValues(
        currentValues.filter((value) => value.value !== optionValue),
      );
    } else {
      const newOption = optionList.find(
        (option) => option.value === optionValue,
      );
      if (newOption) setCurrentValues([...currentValues, newOption]);
    }
  };

  const renderSelectedOptions = () => {
    if (!currentValues || currentValues.length === 0) {
      return (
        <BodyText size={BODY_TEXT_SIZES.X_SMALL} color="text-tertiary">
          {messages["settings"]["companyTabs"]["customizeFields"]["empty"]}
        </BodyText>
      );
    }

    const first = currentValues[0];
    const overflow = currentValues.length - 1;

    return (
      <div className="flex items-center gap-xs">
        <Tag
          label={first.label}
          isHoverable={false}
          style={{
            color: first.color,
            background: getOptionBackgroundColor(first.color),
          }}
        />
        {overflow > 0 && (
          <Tag
            label={`+${overflow}`}
            isHoverable={false}
            className="bg-accent text-secondary"
          />
        )}
      </div>
    );
  };

  const renderOption = (item: {
    _id: string;
    label: string;
    value: string;
    color: string;
  }) => (
    <div className="flex items-center gap-xs">
      <Tag
        label={item.label}
        isHoverable={false}
        style={{
          color: item.color,
          background: getOptionBackgroundColor(item.color),
        }}
      />
    </div>
  );

  const SelectComponent = usePortal ? Select3MultiPortal : Select3Multi;

  return (
    <div className="flex items-center gap-sm py-sm custom-field-row">
      <BodyText
        size={BODY_TEXT_SIZES.X_SMALL}
        color="text-secondary"
        title={field?.label}
        className="w-[140px] flex-shrink-0 overflow-hidden text-ellipsis whitespace-nowrap"
      >
        {field?.label}:
      </BodyText>
      <div className={"flex-1 min-w-0 " + (!isEditable ? "pl-1.5" : "")}>
        <SelectComponent
          avatar={null}
          buttonModifier={`custom-field-select-trigger ${
            currentValues.length > 0 ? "has-value" : ""
          }`}
          className="!mx-0 w-full"
          containerTitle={`${field?.label}:`}
          defaultValues={currentValues}
          colorKey="color"
          disabled={!isEditable}
          displayRow={false}
          error={null}
          isOpenDropdown={isDropDownOpen}
          setIsOpenDropdown={onToggle}
          label=""
          labelUppercase={false}
          options={optionList}
          onClick={onClick}
          isRemoveOption={true}
          removeAll={() => setCurrentValues([])}
          searchPlaceholder={messages["common"]["searchText"]}
          placeholder={
            messages["settings"]["companyTabs"]["customizeFields"]["empty"]
          }
          renderOption={renderOption}
          onClose={onClose}
          searchable
          status={null}
          areGroupedOptions={false}
          renderSelectedOptions={renderSelectedOptions}
        />
      </div>
    </div>
  );
};

export default CustomMultiSelectField;
